import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { 
  Shield, Users, Mail, Send, Search, DollarSign, 
  LogOut, ChevronRight, TrendingUp, UserCheck
} from "lucide-react";

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const adminToken = localStorage.getItem("admin_token");
  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchStats();
  }, [adminToken, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate("/admin");
      }
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Portal</h1>
              <p className="text-sm text-slate-400">Campaign Pro</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{adminUser.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-800/50 border-b border-slate-700 px-6 py-2">
        <div className="max-w-7xl mx-auto flex gap-4">
          <Link 
            to="/admin/dashboard"
            className="px-4 py-2 text-white bg-slate-700 rounded-lg text-sm font-medium"
          >
            Dashboard
          </Link>
          <Link 
            to="/admin/users"
            className="px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
          >
            Users
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold text-white mb-6">Platform Overview</h2>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.users?.total || 0}
            subtitle={`${stats?.users?.verified || 0} verified`}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="New This Week"
            value={stats?.users?.new_this_week || 0}
            subtitle="user registrations"
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Emails Sent"
            value={stats?.content?.emails_sent || 0}
            subtitle={`${stats?.content?.campaigns_sent || 0} campaigns`}
            icon={Send}
            color="orange"
          />
          <StatCard
            title="Outscraper Searches"
            value={stats?.outscraper?.total_searches || 0}
            subtitle="lead searches"
            icon={Search}
            color="purple"
          />
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Content Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Contacts</span>
                  <span className="text-white font-medium">{stats?.content?.contacts || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Templates</span>
                  <span className="text-white font-medium">{stats?.content?.templates || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Campaigns</span>
                  <span className="text-white font-medium">{stats?.content?.campaigns || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Billing (Coming Soon)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Revenue</span>
                  <span className="text-white font-medium">${stats?.billing?.total_revenue?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Active Subscriptions</span>
                  <span className="text-white font-medium">{stats?.billing?.active_subscriptions || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Plan</span>
                  <span className="text-emerald-400 font-medium">Free Tier</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Link to="/admin/users">
                  <Button variant="outline" className="w-full justify-between border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
                    Manage Users
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button variant="outline" className="w-full justify-between border-slate-600 text-slate-500 cursor-not-allowed" disabled>
                  Billing Settings
                  <span className="text-xs bg-slate-700 px-2 py-0.5 rounded">Soon</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colors = {
    blue: "bg-blue-500/10 text-blue-400",
    green: "bg-emerald-500/10 text-emerald-400",
    orange: "bg-orange-500/10 text-orange-400",
    purple: "bg-purple-500/10 text-purple-400",
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-slate-400 text-sm">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value.toLocaleString()}</p>
            <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboardPage;
